import type { Me } from '@/api/session';
import type { WireCoreOrigin } from '@/api/wire';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';

/**
 * What every builder in this part of the app needs to say "you", to know
 * how long ago something was, and to name a person the reader is not
 * (ADR-0155). Kept pure: `now` is passed in, so a test can fix the day and
 * a tip that depends on how long something has waited reads the same twice.
 */

export type Viewer = Pick<Me, 'humanId' | 'department' | 'leads' | 'isSuperadmin'> & { now?: number | undefined };

export const viewerOf = (me: Me): Viewer => ({
  humanId: me.humanId,
  department: me.department,
  leads: me.leads,
  isSuperadmin: me.isSuperadmin,
});

export const nowOf = (v: Viewer): number => v.now ?? Date.now();

/** Whether the reader leads the department (a superadmin decides for every one). */
export const leadsOrAdmin = (v: Viewer, department: string | null | undefined): boolean =>
  v.isSuperadmin || (department !== null && department !== undefined && v.leads.includes(department));

const DAY = 86_400_000;

/** A date in words, as every list writes it: "3 Sep 2026". */
export function dayOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Whole days since, never negative; null for a date that is not one. */
export function daysSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((now - t) / DAY));
}

/** Calendar days from today to a date (negative once it has passed): 0 is today, whatever the hour. */
export function daysUntil(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(t)) return null;
  return Math.round((t - Date.parse(`${new Date(now).toISOString().slice(0, 10)}T00:00:00Z`)) / DAY);
}

/** "today", "yesterday", "6 days ago", "3 weeks ago", "2 months ago". */
export function agoWords(iso: string | null | undefined, now: number): string | null {
  const d = daysSince(iso, now);
  if (d === null) return null;
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d} days ago`;
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`;
  if (d < 730) return `${Math.floor(d / 30)} months ago`;
  return `${Math.floor(d / 365)} years ago`;
}

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * A person the platform names by id and display name: the reader is "You",
 * anybody else is their name and what they are for here. Null when the
 * record holds nobody.
 */
export function personParty(v: Viewer, who: Who, humanId: string | null | undefined, name: string | null | undefined, role?: string): Party | null {
  if (!humanId && !name) return null;
  if (humanId && humanId === v.humanId) return { ...who.me, ...(role ? { role } : {}) };
  if (!name) return null;
  return { name, kind: 'person', ...(role ? { role } : {}) };
}

/** Parties to ask: nobody empty, never the reader, never the same name twice. */
export function others(parties: Array<Party | null | undefined>): Party[] {
  const seen = new Set<string>();
  const out: Party[] = [];
  for (const p of parties) {
    if (!p || p.name === 'You') continue;
    const key = `${p.kind}:${p.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/** The first name, for a sentence that is about a person rather than to them. */
export const firstOf = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** What a counterparty is to us, as a sentence: the platform's own words for it (ADR-0063). */
export function relationshipSentence(r: string): string {
  switch (r) {
    case 'client':
      return 'a client: we sell to them';
    case 'vendor':
      return 'a vendor: we buy from them';
    case 'partner':
      return 'a partner: we work with them';
    case 'investor':
      return 'an investor';
    case 'internal':
      return 'part of us, in another guise';
    default:
      return 'not yet approved as anything';
  }
}


const ORIGIN_WORDS: Record<WireCoreOrigin['source'], string> = {
  mail: 'Mail from them was held, and promoted to a contact',
  prospect: 'Filed from a prospect when its company was approved',
  real_data: 'Loaded from Norum’s own research',
  test_world: 'Part of the test world',
  receipt: 'First on record when a person approved it',
};

/**
 * Where a core record came from and who brought it (ADR-0165): `undefined`
 * on an older reply, so the panel leaves both lines out; `null` when the
 * platform holds no origin, which reads as not recorded. Never guessed.
 */
export function originOf(
  origin: WireCoreOrigin | null | undefined,
  who: Who,
  v: Viewer,
): { source: ItemContext['source']; broughtBy: ItemContext['broughtBy']; department: string | null } {
  if (origin === undefined) return { source: undefined, broughtBy: undefined, department: null };
  if (origin === null) return { source: null, broughtBy: null, department: null };
  const when = dayOf(origin.at) ?? undefined;
  const department = origin.department ? who.label(origin.department) : null;
  const label = `${ORIGIN_WORDS[origin.source]}${department ? `, in ${department}` : ''}`;
  const party: Party | null =
    origin.byKind === 'agent' && origin.by
      ? who.actor(origin.by)
      : origin.byKind === 'person'
        ? personParty(v, who, origin.byHuman, origin.by)
        : null;
  return {
    source: { label, ...(when ? { when } : {}) },
    broughtBy: party ? { ...party, ...(department ? { role: `for ${department}` } : {}), ...(when ? { when } : {}) } : null,
    department,
  };
}
